/* eslint-disable jsx-a11y/anchor-is-valid */
import {useState, useEffect} from 'react'
import {useFormik} from 'formik'
import * as Yup from 'yup'
import clsx from 'clsx'
import {Link, useLocation, useNavigate} from 'react-router-dom'
import axios from 'axios'
import Swal from 'sweetalert2'
import {useClientPortalAuth, ClientPortalAuthProvider} from '../core/ClientPortalAuth'
import {AuthAuroraShell, AuthBrandHeader} from './AuthAurora'

// Criar instância do axios sem interceptors globais para o portal do cliente
const clientPortalAxios = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8000',
  headers: {
    'Content-Type': 'application/json',
  },
})

const initialValues = {
  email: '',
  password: '',
}

const loginSchema = Yup.object().shape({
  email: Yup.string().email('Formato de email inválido').required('Email é obrigatório'),
  password: Yup.string().min(3, 'Mínimo de 3 caracteres').required('Senha é obrigatória'),
})

function ClientPortalLoginInner() {
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const {user} = useClientPortalAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const successMessage = (location.state as {successMessage?: string} | null)?.successMessage

  // Redirecionar se já estiver logado
  useEffect(() => {
    if (user) {
      navigate('/area-do-cliente/dashboard', {replace: true})
    }
  }, [user, navigate])

  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      setLoading(true)
      setStatus('') // Limpar status anterior
      try {
        const response = await clientPortalAxios.post('/api/client-portal/auth/login', {
          email: values.email,
          password: values.password,
        })

        if (response.data.success && response.data.api_token) {
          // Salvar token e dados do usuário no localStorage
          localStorage.setItem('client_portal_token', response.data.api_token)
          localStorage.setItem('client_portal_user', JSON.stringify(response.data.user))

          Swal.fire({
            icon: 'success',
            title: 'Login realizado com sucesso!',
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
            customClass: {
              popup: 'bg-light-success',
              title: 'text-success',
            },
          })

          // Redirecionar para área do cliente
          navigate('/area-do-cliente/dashboard')
        } else {
          const errorMsg = response.data?.message || 'Erro ao realizar login. Tente novamente.'
          setStatus(errorMsg)
          setSubmitting(false)
          setLoading(false)
        }
      } catch (error: any) {
        console.error('Erro completo no login:', error)
        console.error('error.response:', error.response)
        console.error('error.response?.data:', error.response?.data)

        let errorMessage = 'Erro ao realizar login. Verifique suas credenciais.'

        // Verificar se há resposta do servidor
        if (error.response?.data) {
          const responseData = error.response.data
          console.log('Dados da resposta do backend:', responseData)

          // Priorizar mensagem do backend
          if (responseData.message) {
            errorMessage = responseData.message
          } else if (responseData.error) {
            errorMessage = responseData.error
          } else if (typeof responseData === 'string') {
            errorMessage = responseData
          }
        } else if (error.message) {
          // Ignorar mensagens genéricas sobre token
          if (!error.message.toLowerCase().includes('token')) {
            errorMessage = error.message
          }
        }

        console.log('Mensagem de erro que será exibida:', errorMessage)
        setStatus(errorMessage)
        setSubmitting(false)
        setLoading(false)
      }
    },
  })

  // Se já estiver logado, não renderizar o formulário
  if (user) {
    return null
  }

  return (
    <AuthAuroraShell>
      <AuthBrandHeader badge='Portal do Cliente' />

      <form
        className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'
        noValidate
        id='kt_client_portal_login_form'
        onSubmit={formik.handleSubmit}
      >
        {/* begin::Heading */}
        <div className='text-center mb-8'>
          <h1 className='text-dark fw-bolder mb-2'>Login</h1>
          <div className='text-gray-500 fw-semibold fs-6'>
            Entre com suas credenciais para acessar o portal
          </div>
        </div>
        {/* end::Heading */}

        {successMessage && (
          <div className='mb-lg-15 alert alert-success'>
            <div className='alert-text font-weight-bold'>{successMessage}</div>
          </div>
        )}

        {formik.status && (
          <div className='mb-lg-15 alert alert-danger'>
            <div className='alert-text font-weight-bold'>{formik.status}</div>
          </div>
        )}

        {/* begin::Form group Email */}
        <div className='fv-row mb-6'>
          <label className='form-label fw-bolder text-dark fs-6'>Email</label>
          <input
            placeholder='Email'
            type='email'
            autoComplete='off'
            {...formik.getFieldProps('email')}
            className={clsx(
              'form-control bg-transparent',
              {'is-invalid': formik.touched.email && formik.errors.email},
              {
                'is-valid': formik.touched.email && !formik.errors.email,
              }
            )}
          />
          {formik.touched.email && formik.errors.email && (
            <div className='fv-plugins-message-container'>
              <div className='fv-help-block'>
                <span role='alert'>{formik.errors.email}</span>
              </div>
            </div>
          )}
        </div>
        {/* end::Form group */}

        {/* begin::Form group Password */}
        <div className='fv-row mb-5'>
          <label className='form-label fw-bolder text-dark fs-6'>Senha</label>
          <input
            type={showPassword ? 'text' : 'password'}
            placeholder='Senha'
            autoComplete='off'
            {...formik.getFieldProps('password')}
            className={clsx(
              'form-control bg-transparent',
              {
                'is-invalid': formik.touched.password && formik.errors.password,
              },
              {
                'is-valid': formik.touched.password && !formik.errors.password,
              }
            )}
          />
          {formik.touched.password && formik.errors.password && (
            <div className='fv-plugins-message-container'>
              <div className='fv-help-block'>
                <span role='alert'>{formik.errors.password}</span>
              </div>
            </div>
          )}
          <div className='text-end mt-2'>
            <Link to='/auth/area-do-cliente/forgot-password' className='link-primary fs-6 fw-bold'>
              Esqueceu a senha?
            </Link>
          </div>
        </div>
        {/* end::Form group */}

        {/* begin::Form group */}
        <div className='text-center'>
          <button
            type='submit'
            id='kt_client_portal_login_submit'
            className='btn btn-lg btn-primary w-100 mb-3'
            disabled={formik.isSubmitting || !formik.isValid}
          >
            {!loading && <span className='indicator-label'>Entrar</span>}
            {loading && (
              <span className='indicator-progress' style={{display: 'block'}}>
                Aguarde...{' '}
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
        </div>
        {/* end::Form group */}
      </form>

      <p className='tr-auth-security-note text-center mt-6 mb-0'>
        <i className='bi bi-shield-check me-2'></i>
        Esta é uma área segura para acesso ao portal do cliente
      </p>
    </AuthAuroraShell>
  )
}

export function ClientPortalLogin() {
  return (
    <ClientPortalAuthProvider>
      <ClientPortalLoginInner />
    </ClientPortalAuthProvider>
  )
}
